import { getBuyerId, getGuestId, getUserType } from 'dibs-cookie-jar';
import { type FC, useEffect, useCallback, useRef, Suspense } from 'react';
import { useQueryLoader, type PreloadedQuery } from 'react-relay';
import { dibsLazy } from 'dibs-lazy/exports/dibsLazy.ts';
import { CUSTOMER_TYPE_TRADE } from 'dibs-buyer-layout/exports/userStorageConstants.ts';
import { getUserSessionCountryCode } from 'dibs-regional-info/src/regionalInfoHelpers.ts';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';
// components
import VisibilityTracker from 'dibs-visibility-tracker/exports/VisibilityTracker.tsx';
import { PdpRespMarketplaceLoadingCarousel } from '../PdpRespMarketplaceLoadingCarousel/PdpRespMarketplaceLoadingCarousel.tsx';

// lazy components
const PdpRespMarketplaceLayoutSecondaryLazy = dibsLazy(
    'PdpRespMarketplaceLayoutSecondary',
    () =>
        import(
            /* webpackChunkName: "PdpRespMarketplaceLayoutSecondary" */ './PdpRespMarketplaceLayoutSecondary.tsx'
        )
);

// types
import {
    pdpRespMarketplaceSecondaryRootQuery,
    type pdpRespMarketplaceSecondaryRootQueryType,
    type pdpRespMarketplaceSecondaryRootQuery$variables as SecondaryRelayVariables,
} from '../../../queries/pdpRespMarketplaceSecondaryRoot.ts';

const LoadingCarousel: FC = () => {
    return (
        <>
            <PdpRespMarketplaceLoadingCarousel />
            <PdpRespMarketplaceLoadingCarousel />
        </>
    );
};

export const PdpRespMarketplaceLayoutSecondaryRenderer: FC<{
    serverSecondaryQueryRef?: PreloadedQuery<pdpRespMarketplaceSecondaryRootQueryType> | null;
    relayVariables: SecondaryRelayVariables;
}> = ({ relayVariables: serverRelayVariables, serverSecondaryQueryRef }) => {
    const [secondaryQueryRef, loadSecondaryQuery] =
        useQueryLoader<pdpRespMarketplaceSecondaryRootQueryType>(
            pdpRespMarketplaceSecondaryRootQuery,
            serverSecondaryQueryRef
        );
    const relayVariables = useRef(serverRelayVariables);
    const layoutVisibilityRef = useRef<HTMLDivElement>(null);
    // Prevents repeated loadSecondaryQuery calls (each disposes the previous PreloadedQuery).
    const hasLoadedRef = useRef(!!serverSecondaryQueryRef);
    const { isBot, isPdpBottomNewSlModuleTestVariant } = useServerVarsContext();

    useEffect(() => {
        const userId = getBuyerId(document.cookie) || '';
        const userType = getUserType(document.cookie);
        const guestId = getGuestId(document.cookie) || '';
        const userCountryCode = getUserSessionCountryCode();

        relayVariables.current = {
            ...serverRelayVariables,
            userId,
            guestId,
            userCountryCode,
            afterDisplay: true,
            afterDisplayAndBtNone: !isBot,
            fetchUser: !!userId,
            isTrade: userType === CUSTOMER_TYPE_TRADE,
            maxSponsoredItems: !isPdpBottomNewSlModuleTestVariant ? 10 : null,
            minSponsoredItemsSteps: isPdpBottomNewSlModuleTestVariant ? [5, 10, 15] : [5],
        };
    }, [isBot, serverRelayVariables, isPdpBottomNewSlModuleTestVariant]);

    const loadQuery = useCallback(() => {
        if (hasLoadedRef.current) {
            return;
        }
        hasLoadedRef.current = true;
        loadSecondaryQuery(relayVariables.current);
    }, [loadSecondaryQuery]);

    useEffect(() => {
        if (hasLoadedRef.current || secondaryQueryRef) {
            return undefined;
        }
        window.addEventListener('scroll', loadQuery);
        return () => {
            window.removeEventListener('scroll', loadQuery);
        };
    }, [loadQuery, secondaryQueryRef]);

    return (
        <>
            <VisibilityTracker
                elementRef={layoutVisibilityRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        loadQuery();
                        window.removeEventListener('scroll', loadQuery);
                    }
                }}
            />
            <div ref={layoutVisibilityRef} />
            {/* secondaryQueryRef exists if initial queryRef was provided on server (for bots) or load query was called on client */}
            {secondaryQueryRef ? (
                <Suspense fallback={<LoadingCarousel />}>
                    <PdpRespMarketplaceLayoutSecondaryLazy
                        queryRef={secondaryQueryRef}
                        relayVariables={relayVariables.current}
                    />
                </Suspense>
            ) : (
                <LoadingCarousel />
            )}
        </>
    );
};
